import {
  DueDateDayTypes,
  DateOperators,
  DueDateOperators,
  DueDateReferenceDates,
  TaskStatus,
  TASK_STAGE_OPTIONS_IN_ORDER,
} from "@eave/shared/types/api";

// Assumes tasksList are sorted by index_order ASC.
// By default, add a new task as the first task (ie greatest index_order),
// within the current stage.
// ReferenceTaskShape[] | TaskShape[]
export const newTaskDefaultIndexOrder = (tasksList: any[], stage: string) => {
  if (!tasksList || tasksList.length === 0) {
    return 0;
  }

  const stageIndex = TASK_STAGE_OPTIONS_IN_ORDER.findIndex(configStage => configStage === stage);

  if (stageIndex === -1) {
    if (stage === "Pinned tasks") {
      return 0;
    }
    throw new Error(
      `'${stage}' is not a valid stage. valid stages are: ${TASK_STAGE_OPTIONS_IN_ORDER}`,
    );
  }

  for (let i = stageIndex; i >= 0; i--) {
    const currentStage = TASK_STAGE_OPTIONS_IN_ORDER[i];
    const tasksInStage = tasksList.filter(t => t.stage === currentStage);
    if (tasksInStage.length > 0) {
      return tasksInStage[0].index_order;
    }
  }

  return 0;
};

export const getTableHeadersForStage = stage => {
  return [
    {
      header: "Status",
      key: `status-${stage}`,
    },
    {
      header: "Category",
      key: `category-${stage}`,
      style: { width: "20%" },
    },
    {
      header: "Name",
      key: `name-${stage}`,
      style: { width: "30%" },
    },
    {
      header: "Due Date (YYYY-MM-DD)",
      key: `resolved-due-date-${stage}`,
      style: { width: "10%" },
    },
    {
      header: "Pinned",
      key: `pinned`,
    },
  ];
};

export const isComplete = task =>
  task.status === TaskStatus.Cleared || task.status === TaskStatus.NotApplicable;

export const dueDateParamsToDisplayString = (params): string => {
  const { days, type_of_day, before_or_after, reference_date } = params;
  const placeholderStr = "___";
  return `${days || placeholderStr} ${DueDateDayTypes[type_of_day] || placeholderStr} ${
    DueDateOperators[before_or_after] || placeholderStr
  } ${DueDateReferenceDates[reference_date] || placeholderStr}`;
};

export const dateParametersToDisplayString = (params): string => {
  const {
    reference_date,
    date_comparison,
    days,
    type_of_day,
    before_or_after,
    other_reference_date,
  } = params;

  const placeholderStr = "___";

  if (date_comparison === DateOperators.Null || date_comparison === DateOperators.NotNull) {
    return `${DueDateReferenceDates[reference_date] || placeholderStr} is ${
      date_comparison || placeholderStr
    }`;
  }
  const hasModifier = days || type_of_day || before_or_after;
  if (hasModifier) {
    return `${DueDateReferenceDates[reference_date] || placeholderStr} is ${
      date_comparison || placeholderStr
    } ${days || placeholderStr} ${DueDateDayTypes[type_of_day] || placeholderStr} ${
      DueDateOperators[before_or_after] || placeholderStr
    } ${DueDateReferenceDates[other_reference_date] || placeholderStr}`;
  }

  return `${reference_date || placeholderStr} is ${date_comparison || placeholderStr} ${
    DueDateReferenceDates[other_reference_date] || placeholderStr
  }`;
};

// Returns the `items` used in the <Select> for task reordering
export const taskOrderItems = (tasks, formState, defaultIndexOrderOverride?: number) => {
  const addToTop = {
    key: "add-to-top",
    text: "(Add to top)",
    value: defaultIndexOrderOverride || formState.default_index_order,
  };

  // tasks in the current stage, minus the task being edited in the modal --
  // since a task can't be the next task after itself.
  const itemsInStage = tasks
    ? tasks
        .filter(task => task.stage === formState.stage && task.id !== formState.id)
        .map(task => ({
          key: `${task.index_order}-task-order`,
          text: task.name,
          label: task.name,
          value: task.index_order,
        }))
    : [];

  return [addToTop, ...itemsInStage];
};
// assumes tasksList are sorted ASC by index_order
export const taskOrderInitialSelectedItem = (tasks, formState) => {
  // items in current stage, minus item being edited.
  const [addToTop, ...existingTasks] = taskOrderItems(tasks, formState);

  // EditingState.CREATE:
  // the to-be-created task, and the item after it, share an index order.
  // the item after's index order is incremented when this item is actually persisted.

  // EditingState.UPDATE:
  // current item is already persisted.
  // so, next item has a greater index order already.

  // so, either way, find first with a >= cur's index order, since:
  // - if creating:
  // want ===, which is what we'll get, since next item is in taskOrderItems.

  // - if updating:
  // want >, which is what we'll get, since cur item is removed from taskOrderItems.

  const nextTask = existingTasks.find(t => t.value >= formState.index_order);

  return nextTask || addToTop;
};
